import { useEffect, useMemo, useRef, useState } from 'react';
import responseHandler from 'src/utils/response_handler';
import { getFullEvent } from 'src/api/events';
import { useParams } from 'react-router';
import { useToast } from 'src/contexts/toast';
import { calculateTargetCameraPosition, centerCamera, initScene } from 'src/pages/plan/utils/scene';
import {
	createBoothLabelsForPath,
	createBooths,
	createFloor,
	createPublic,
	createWalls,
	detectConnectedCompaniesToPath,
} from 'src/pages/plan/utils/plan';
import { createPath, drawGraph, makeGraph, makeMarker, makeSource } from 'src/pages/plan/utils/graph';
import LoadingIndicator from 'src/components/ui/loading_indicator';
import cls from 'src/utils/class_names';
import styles from './index.sass';
import planConfig from 'src/config/plan';
import { v4 as uuid } from 'uuid';
import * as THREE from 'three';
import Helmet from 'react-helmet';
import { mergeBoothLabels } from 'src/utils/merge_booth_labels';
import aStar from 'src/utils/graph/a_star';
import Polygon from 'src/utils/shape_processor/classes/polygon';
import { getEventFavIcon, getEventLogo } from 'src/utils/event_images';
import { findNearestIndicators } from 'src/pages/plan/utils/indicators';
import { useSearchParams } from 'react-router-dom';

function PlanPage() {
	const params = useParams();
	const [searchParams] = useSearchParams();
	const { toast } = useToast();

	const eventId = parseInt(params.eventId || '', 10);
	const startNodeId = parseInt(params.startNodeId || '', 10);
	const endNodeId = parseInt(params.endNodeId || '', 10);

	const [sourceSearch, setSourceSearch] = useState('');
	const [selectedSource, setSelectedSource] = useState(null as any);

	const [destinationSearch, setDestinationSearch] = useState('');
	const [selectedDestination, setSelectedDestination] = useState(null as any);

	const [target, setTarget] = useState({ label: null as any, boothNo: null as any, indicator: null as any });

	const [loading, setLoading] = useState(planConfig.init.event);
	const [state, setState] = useState(null as any);
	const [sponsorState, setSponsorState] = useState({
		uuid: uuid(),
		open: false,
		header: true,
		loading: false,
		sponsor: null as any,
	});
	const [showReplay, setShowReplay] = useState(false);
	const [openSourceModal, setOpenSourceModal] = useState(false);
	const [openDestinationModal, setOpenDestinationModal] = useState(false);

	const canvasRef = useRef(null as any);
	const initRef = useRef({
		scene: false,
		camera: false,
		marker: false,

		source: false,

		floor: false,
		border: false,
		public: false,

		booths: false,

		search: false,
		autoNavigationDone: !endNodeId,
	});
	const contextRef = useRef(null as any);
	const pathRef = useRef({
		path: null,
		pathMesh: null,
		target: null,
		targetMesh: null,
	} as any);
	const navigationRef = useRef({
		marker: null,

		initialCameraPosition: null,
		initialCameraQuaternion: null,
		currentCameraPosition: null,
		currentCameraQuaternion: null,
		targetCameraPosition: null,
		targetCameraQuaternion: null,

		mode: null,
		startTime: null,
		duration: null,
		pathCurve: null,
		cameraCurve: null,
		distance: null,
		pathLength: null,
		simplifiedPath: null,

		labelsGroup: null,
	} as any);
	const inputSourceRef = useRef(null as any);
	const inputDestinationRef = useRef(null as any);

	// Request Effect
	useEffect(() => {
		if (planConfig.init.event) {
			// Fetch Full Event and Graph
			void responseHandler(getFullEvent(eventId), toast).then((event: any) => {
				// Build the Graph
				const [graphNodesMap, companyNodesMap] = planConfig.init.graph
					? makeGraph(event.graph.nodes, event.graph.links)
					: [];

				// Connect Booths to the Graph
				const companyBoothMap: any = {};
				if (planConfig.init.companies) {
					const companyBooths = event.booths.filter((booth) => Boolean(booth.company_id));

					companyBooths.forEach((booth) => {
						const nodeId = companyNodesMap[booth.company_id];
						const node = graphNodesMap[`${eventId}-${nodeId}`];

						if (nodeId && node) {
							companyBoothMap[booth.company_id] = {
								nodeId,
								node,
								company: null,
								booth,
								polygon: Polygon.fromCoords(booth.bounds.map((p) => [p.x, p.y]).flat()),
							};
						}
					});
				}

				const searchItems: any[] = [];

				if (planConfig.init.search) {
					const generateBoothLabels = (booths: any[]) => {
						let label = '';
						if (booths?.length > 0) {
							for (const booth of booths) {
								label += `${booth.hall.name}-${booth.no} `;
								label += `${booth.hall.name}_${booth.no} `;
								label += `${booth.hall.name}/${booth.no} `;
								label += `${booth.hall.name} ${booth.no} `;
								label += `${booth.hall.name}${booth.no} `;
							}
						}
						return label;
					};

					event.companies.forEach((company) => {
						if (companyBoothMap[company.id]) {
							companyBoothMap[company.id].company = company;

							const { booth, nodeId, node } = companyBoothMap[company.id];

							searchItems.push({
								type: 'booth',
								label: company.legal_name_fa,
								key: `${company.legal_name_fa} ${company.legal_name_en} ${company.brand_name_fa} ${
									company.brand_name_en
								} ${generateBoothLabels(company.booths)}`.toLowerCase(),
								company,
								booth,
								node,
								nodeId,
							});

							company.subsidiaries?.forEach((subCompany) => {
								searchItems.push({
									type: 'booth',
									label: subCompany.legal_name_fa,
									key: `${company.legal_name_fa} ${company.legal_name_en} ${company.brand_name_fa} ${
										company.brand_name_en
									} ${generateBoothLabels(company.booths)} ${subCompany.legal_name_fa} ${subCompany.legal_name_en} ${
										subCompany.brand_name_fa
									} ${subCompany.brand_name_en} ${generateBoothLabels(subCompany.booths)}`.toLowerCase(),
									company,
									booth,
									node,
									nodeId,
								});
							});
						}
					});
				}

				setState({
					event,
					graph: graphNodesMap,
					companyBoothMap,

					search: searchItems,
				});
			});
		}

		if (planConfig.search.allowManual) {
			window.addEventListener('click', () => {
				if (navigationRef.current.mode === 'freeze') {
					navigationRef.current.mode = 'finished';

					setShowReplay(false);
				}
			});
		}
	}, []);

	function animationLoop(t) {
		const { scene, camera, controls, renderer } = contextRef.current;

		if (controls) {
			controls.update();
		}

		if (navigationRef.current.mode) {
			const { mode } = navigationRef.current;
			if (mode === 'started') {
				const { pathLength } = navigationRef.current;

				const duration = (pathLength / planConfig.navigation.speed) * 1000;

				navigationRef.current = {
					...navigationRef.current,

					mode: 'running',
					startTime: t,
					duration,
				};
			} else if (mode === 'running') {
				const { marker, startTime, duration, pathCurve, cameraCurve } = navigationRef.current;
				const time = t - startTime;
				const percent = Math.min(time / duration, 1);

				const p1 = pathCurve.getPointAt(percent);
				marker.position.copy(p1);

				const cp = cameraCurve.getPointAt(percent);
				camera.position.copy(cp);
				camera.lookAt(p1);

				if (percent < 1) {
					const p2 = pathCurve.getPointAt(Math.min(percent + 0.0000001, 1));
					marker.lookAt(p2);
				} else {
					navigationRef.current.mode = 'ended';
				}
			} else if (mode === 'ended') {
				const { camera } = contextRef.current;

				navigationRef.current = {
					...navigationRef.current,

					mode: 'ending',
					startTime: t,

					currentCameraPosition: camera.position.clone(),
					currentCameraQuaternion: camera.quaternion.clone(),
				};
			} else if (mode === 'ending') {
				const {
					startTime,
					targetCameraPosition,
					targetCameraQuaternion,
					currentCameraPosition,
					currentCameraQuaternion,
				} = navigationRef.current;
				const { camera } = contextRef.current;

				let time = t - startTime;

				if (time >= planConfig.navigation.cameraResetDelay) {
					time -= planConfig.navigation.cameraResetDelay;

					const percent = Math.min(time / planConfig.navigation.cameraResetDuration, 1);

					if (percent <= 1) {
						const position = currentCameraPosition.clone().lerp(targetCameraPosition, percent);
						const quaternion = currentCameraQuaternion.clone().slerp(targetCameraQuaternion, percent);

						camera.position.copy(position);
						camera.quaternion.copy(quaternion);

						if (percent === 1) {
							navigationRef.current.mode = 'freeze';

							setShowReplay(true);
						}
					}
				}
			} else if (mode === 'finished') {
				const resetPath = () => {
					setTarget({ label: null, boothNo: null, indicator: null });

					const boothsConfig = planConfig.objects.booths;
					const placesConfig = planConfig.objects.places;

					contextRef.current.scene.remove(pathRef.current.pathMesh);

					if (navigationRef.current.labelsGroup) {
						contextRef.current.scene.remove(navigationRef.current.labelsGroup);
					}

					if (pathRef.current.sourceMesh?.material) {
						if (pathRef.current.source.type === 'booth') {
							pathRef.current.sourceMesh.material[0].color = new THREE.Color(boothsConfig.faceColor);
							pathRef.current.sourceMesh.material[1].color = new THREE.Color(boothsConfig.extrusionColor);
						} else if (pathRef.current.source.type === 'place') {
							pathRef.current.sourceMesh.material[0].color = new THREE.Color(placesConfig.faceColor);
							pathRef.current.sourceMesh.material[1].color = new THREE.Color(placesConfig.extrusionColor);
						}
					}

					if (pathRef.current.targetMesh?.material) {
						if (pathRef.current.target.type === 'booth') {
							pathRef.current.targetMesh.material[0].color = new THREE.Color(boothsConfig.faceColor);
							pathRef.current.targetMesh.material[1].color = new THREE.Color(boothsConfig.extrusionColor);
						} else if (pathRef.current.target.type === 'place') {
							pathRef.current.targetMesh.material[0].color = new THREE.Color(placesConfig.faceColor);
							pathRef.current.targetMesh.material[1].color = new THREE.Color(placesConfig.extrusionColor);
						}
					}
					contextRef.current.camera.position.copy(navigationRef.current.initialCameraPosition);
					contextRef.current.camera.quaternion.copy(navigationRef.current.initialCameraQuaternion);
				};

				if (planConfig.sponsorsScreen.enabled && state.event?.sponsors?.length > 0 && sponsorState.sponsor) {
					setSponsorState((current) => ({
						...current,
						open: true,
						loading: false,
					}));

					setTimeout(resetPath, planConfig.sponsorsScreen.duration / 2);

					setTimeout(() => {
						setSponsorState((current) => ({
							...current,
							open: false,
							header: true,
							loading: false,
						}));
					}, planConfig.sponsorsScreen.duration);
				} else {
					resetPath();
				}

				navigationRef.current.mode = null;
			}
		}

		renderer.render(scene, camera);
	}

	useEffect(() => {
		if (contextRef.current && state.event) {
			contextRef.current.renderer.setAnimationLoop(animationLoop);
		}
	}, [state, sponsorState]);

	// Scene Effect
	useEffect(() => {
		if (!initRef.current.scene && canvasRef.current) {
			initRef.current.scene = true;
			contextRef.current = initScene(canvasRef.current, animationLoop as any);
		}

		const { scene } = contextRef.current;

		if (state && scene) {
			if (!initRef.current.camera && state.event && planConfig.init.camera) {
				initRef.current.camera = true;

				const { x: width, y: height } = state.event.map_bounds[1];
				contextRef.current.width = width;
				contextRef.current.height = height;

				centerCamera(0, 0, contextRef.current);

				navigationRef.current.initialCameraPosition = contextRef.current.camera.position.clone();
				navigationRef.current.initialCameraQuaternion = contextRef.current.camera.quaternion.clone();
			}

			if (!initRef.current.floor && state.event.floor && planConfig.init.floor) {
				initRef.current.floor = true;

				const floorGroup = createFloor(state.event.floor);
				scene.add(floorGroup);
			}

			if (!initRef.current.border && state.event.border && planConfig.init.walls) {
				initRef.current.border = true;

				const wallsGroup = createWalls(state.event.border);
				scene.add(wallsGroup);
			}

			let searchStateUpdate: any[] = [];
			let companyBoothMapStateUpdate: Record<any, any> = [];

			if (!initRef.current.public || !initRef.current.booths) {
				searchStateUpdate = [...state.search];
				companyBoothMapStateUpdate = { ...state.companyBoothMap };
			}

			if (!initRef.current.public && state.event.public && planConfig.init.places) {
				initRef.current.public = true;

				const publicGroup = createPublic(state.event.public, ((path: any, mesh: any, geometry: any) => {
					const { nodeId, label, dataset } = path;

					const node = state.graph[`${eventId}-${nodeId}`];

					if (nodeId && node && label) {
						geometry.computeBoundingBox();
						const { min: bMin, max: bMax } = geometry.boundingBox;

						const company = {
							id: 'p' + nodeId,
							legal_name_fa: label,
							legal_name_en: dataset?.name || label,
							brand_name_fa: label,
							brand_name_en: dataset?.name || label,
						};

						companyBoothMapStateUpdate['p' + nodeId] = {
							nodeId,
							company,
							polygon: Polygon.fromCoords([bMin.x, bMin.z, bMax.x, bMin.z, bMax.x, bMax.z, bMin.x, bMax.z]),
							mesh,
							node,
							booth: { node },
						};

						searchStateUpdate.push({
							type: 'place',
							label,
							company,
							key: `${label.toLowerCase()} ${dataset?.name?.toLowerCase()}`,
							node,
							booth: { node },
							nodeId,
							mesh,
						});
					}
				}) as any);
				scene.add(publicGroup);
			}

			if (!initRef.current.booths && planConfig.init.booths) {
				initRef.current.booths = true;

				const boothsGroup = createBooths(state.event.booths, ((booth, mesh) => {
					if (booth.company_id) {
						const companyId = parseInt(booth.company_id, 10);

						companyBoothMapStateUpdate[companyId].mesh = mesh;

						const indexes = searchStateUpdate
							.map((item, index) => {
								if (item.type === 'booth' && item.company.id === companyId) {
									return index;
								}
								return null;
							})
							.filter((index) => index !== null);

						if (indexes.length >= 0) {
							indexes.forEach((index: number) => {
								searchStateUpdate[index] = {
									...searchStateUpdate[index],
									mesh,
								};
							});
						}
					}
				}) as any);

				scene.add(boothsGroup);
			}

			if (planConfig.features.graph) {
				drawGraph(state.graph, contextRef.current);
			}

			if (startNodeId && planConfig.init.source && !initRef.current.source) {
				initRef.current.source = true;
				const startNode = state.graph[`${eventId}-${startNodeId}`];

				scene.add(makeSource(startNode));
			}

			if (!initRef.current.marker) {
				initRef.current.marker = true;

				const marker = makeMarker();
				marker.position.set(0, 0, 0);
				marker.visible = false;
				scene.add(marker);

				navigationRef.current.marker = marker;
			}

			if (searchStateUpdate.length > 0) {
				setState({ ...state, search: searchStateUpdate, companyBoothMap: companyBoothMapStateUpdate });
			}

			setLoading(false);
		}
	}, [state, canvasRef.current]);

	const handleSelect = (item, type: string) => {
		if (type === 'source') {
			setSelectedSource(item);
			setSourceSearch(item.label);
		} else if (type === 'destination') {
			setSelectedDestination(item);
			setDestinationSearch(item.label);
		}

		setOpenSourceModal(false);
		setOpenDestinationModal(false);
	};

	const navigateHandler = (e) => {
		e.preventDefault();
		e.stopPropagation();

		setShowReplay(false);

		if (!selectedSource && !startNodeId) {
			toast.warning('مسیریابی', 'مبدا انتخاب نشده است');
			return;
		}

		if (!selectedDestination) {
			toast.warning('مسیریابی', 'مقصد انتخاب نشده است');
			return;
		}

		if (selectedDestination && (selectedSource || startNodeId)) {
			handleSearch(selectedSource, selectedDestination);
		}
	};

	const handleSearch = (startItem, endItem) => {
		if (endItem) {
			let oldPathContext: any = null;
			if (pathRef.current.path) {
				oldPathContext = { ...pathRef.current };
			}

			if (endItem.nodeId) {
				const path = aStar(
					state.graph[`${eventId}-${startItem?.nodeId || startNodeId}`],
					state.graph[`${eventId}-${endItem.nodeId}`]
				);

				if (path?.length < 2) {
					toast.warning('مسیریابی', 'هیچ مسیری یافت نشد.');
					return;
				}

				// start out of the selectedSource
				// if (startItem?.nodeId) {
				// 	path.shift();
				// }

				const [pathMesh, simplifiedPath, pathCurve, cameraCurve, distance, pathLength] = createPath(
					path,
					planConfig.objects.paths[endItem.type]
				);

				const { position: targetCameraPosition, quaternion: targetCameraQuaternion } = calculateTargetCameraPosition(
					path[0].coords, // first point
					path[path.length - 1].coords, // last point
					contextRef.current.camera,
					contextRef.current.renderer
				);

				pathRef.current = {
					...pathRef.current,

					path,
					pathMesh,
					source: startItem,
					sourceMesh: startItem?.mesh,
					target: endItem,
					targetMesh: endItem.mesh,
				};
				navigationRef.current = {
					...navigationRef.current,

					startTime: null,
					pathCurve,
					cameraCurve,
					distance,
					pathLength,
					simplifiedPath,

					labelsGroup: null,

					targetCameraPosition,
					targetCameraQuaternion,
				};

				const indicator = findNearestIndicators(state.event.indicators, path[path.length - 1].coords);

				const showPath = () => {
					setTarget({
						label: endItem.label,
						boothNo: endItem.company?.booths?.length > 0 ? mergeBoothLabels(endItem.company?.booths) : null,
						indicator,
					});

					const sourceConfig = startItem ? planConfig.objects.source[startItem.type] : null;
					const targetConfig = planConfig.objects.target[endItem.type];
					const boothsConfig = planConfig.objects.booths;
					const placesConfig = planConfig.objects.places;

					if (planConfig.search.animate) {
						const { camera } = contextRef.current;
						const { marker, simplifiedPath, cameraCurve } = navigationRef.current;

						marker.visible = true;
						marker.position.copy(simplifiedPath[0]);
						marker.lookAt(simplifiedPath[1]);

						camera.position.copy(cameraCurve.getPoint(0));
						camera.lookAt(simplifiedPath[0]);
					}

					if (oldPathContext) {
						contextRef.current.scene.remove(oldPathContext.pathMesh);

						if (oldPathContext?.sourceMesh?.material) {
							if (oldPathContext.source.type === 'booth') {
								oldPathContext.sourceMesh.material[0].color = new THREE.Color(boothsConfig.faceColor);
								oldPathContext.sourceMesh.material[1].color = new THREE.Color(boothsConfig.extrusionColor);
							} else if (oldPathContext.source.type === 'place') {
								oldPathContext.sourceMesh.material[0].color = new THREE.Color(placesConfig.faceColor);
								oldPathContext.sourceMesh.material[1].color = new THREE.Color(placesConfig.extrusionColor);
							}
						}

						if (oldPathContext?.targetMesh?.material) {
							if (oldPathContext.target.type === 'booth') {
								oldPathContext.targetMesh.material[0].color = new THREE.Color(boothsConfig.faceColor);
								oldPathContext.targetMesh.material[1].color = new THREE.Color(boothsConfig.extrusionColor);
							} else if (oldPathContext.target.type === 'place') {
								oldPathContext.targetMesh.material[0].color = new THREE.Color(placesConfig.faceColor);
								oldPathContext.targetMesh.material[1].color = new THREE.Color(placesConfig.extrusionColor);
							}
						}
					}

					contextRef.current.scene.add(pathMesh);

					if (startItem?.mesh?.material) {
						startItem.mesh.material[0].color = new THREE.Color(sourceConfig.faceColor);
						startItem.mesh.material[1].color = new THREE.Color(sourceConfig.extrusionColor);
					}

					if (endItem.mesh?.material) {
						endItem.mesh.material[0].color = new THREE.Color(targetConfig.faceColor);
						endItem.mesh.material[1].color = new THREE.Color(targetConfig.extrusionColor);
					}

					if (planConfig.search.label) {
						const { scene, font } = contextRef.current;

						const connectedCompanies = detectConnectedCompaniesToPath(path, state.companyBoothMap);
						const labelsGroup = createBoothLabelsForPath(path, connectedCompanies, font, 8, 0.2, 0xcccccc);

						scene.add(labelsGroup);

						navigationRef.current.labelsGroup = labelsGroup;
					}
				};

				if (planConfig.sponsorsScreen.enabled && state.event.sponsors?.length > 0) {
					const sponsorIndex = state.event.sponsors.indexOf(sponsorState.sponsor);
					const sponsor = state.event.sponsors[(sponsorIndex + 1) % state.event.sponsors.length];
					setSponsorState({
						open: true,
						uuid: uuid(),
						loading: true,
						header: true,
						sponsor,
					});

					if (!planConfig.search.animate) {
						showPath();
						navigationRef.current.mode = 'freeze';

						setShowReplay(true);
					} else {
						setTimeout(showPath, planConfig.sponsorsScreen.duration / 2);
						setTimeout(() => {
							navigationRef.current.mode = 'started';
						}, planConfig.sponsorsScreen.duration + planConfig.navigation.startDelay);
					}

					setTimeout(() => {
						setSponsorState((current) => ({
							...current,
							open: false,
							header: false,
							loading: true,
						}));
					}, planConfig.sponsorsScreen.duration);
				} else {
					showPath();

					if (!planConfig.search.animate) {
						navigationRef.current.mode = 'freeze';

						setShowReplay(true);
					} else {
						navigationRef.current.mode = 'started';
					}
				}
			}
		}

		setOpenSourceModal(false);
		setOpenDestinationModal(false);
	};

	const shareHandler = (e) => {
		e.stopPropagation();

		if (navigator.clipboard) {
			void navigator.clipboard.writeText(
				`${window.location.origin}/plan/${eventId}/${selectedSource?.nodeId || startNodeId}/${
					selectedDestination?.nodeId || endNodeId
				}`
			);

			toast.info('کپی آدرس', 'آدرس مسیر فعلی کپی شد');
		}
	};

	const searchSourceItems = useMemo(
		() =>
			state?.search
				?.filter((item: any) => {
					return sourceSearch?.length >= 1 && item.key.includes(sourceSearch.toLowerCase());
				})
				.slice(0, 10) || [],
		[sourceSearch, state?.search]
	);

	const searchDestinationItems = useMemo(
		() =>
			state?.search
				?.filter((item: any) => {
					return destinationSearch?.length >= 1 && item.key.includes(destinationSearch.toLowerCase());
				})
				.slice(0, 10) || [],
		[destinationSearch, state?.search]
	);

	const inputChangeHandler = (e, type: string) => {
		if (type === 'source') {
			if (e.target.value.length > 0) {
				setOpenSourceModal(true);
			} else {
				setOpenSourceModal(false);
			}
			setSourceSearch(e.target.value);
		} else if (type === 'destination') {
			if (e.target.value.length > 0) {
				setOpenDestinationModal(true);
			} else {
				setOpenDestinationModal(false);
			}
			setDestinationSearch(e.target.value);
		}
	};

	useEffect(() => {}, [loading, sponsorState.open, target]);

	useEffect(() => {
		if (!loading && state?.search?.length > 0 && !initRef.current.search) {
			let startItem;
			if (startNodeId) {
				startItem = startNodeId ? state.search.find((company: any) => company.nodeId === startNodeId) : null;

				if (startItem) {
					setSelectedSource(startItem);
					setSourceSearch(startItem.label);
				}
			}

			if (endNodeId && endNodeId !== startNodeId) {
				const endItem = state.search.find((company: any) => company.nodeId === endNodeId);

				if (endItem) {
					setSelectedDestination(endItem);
					setDestinationSearch(endItem.label);

					if (!initRef.current.autoNavigationDone) {
						initRef.current.autoNavigationDone = true;
						handleSearch(startItem, endItem);
					}
				}
			}

			initRef.current.search = true;
		}
	}, [state, endNodeId, startNodeId, target, sponsorState.open, loading]);

	return (
		<main
			className={cls(styles.planPage, sponsorState.header && planConfig.search.allowManual && styles.planBeforeSearch)}
			onContextMenu={(e) => {
				e.preventDefault();
				e.stopPropagation();
			}}
		>
			<Helmet>
				<title>{state?.event?.short_title_fa}</title>
				<link rel='icon' href={`${process.env.RASAYESH_API_URL}/${getEventFavIcon(state?.event, 'fa')}`} />
			</Helmet>

			<div className={cls(styles.loading, loading && styles.active)}>
				<LoadingIndicator />
			</div>

			<div
				className={cls(styles.sponsorScreen, sponsorState.open && styles.open)}
				style={{ transitionDuration: `${planConfig.sponsorsScreen.transition}ms` }}
			>
				{sponsorState.sponsor && (
					<>
						<img
							className={cls(sponsorState.sponsor.is_event && styles.monotoneImage)}
							src={`${sponsorState.sponsor.is_event ? process.env.RASAYESH_API_URL : process.env.API_URL}/${
								sponsorState.sponsor.logo
							}`}
							alt={sponsorState.sponsor.title}
						/>
						<h2>{sponsorState.sponsor.title}</h2>
						<p>{sponsorState.sponsor.description}</p>

						<div key={sponsorState.uuid} style={{ visibility: sponsorState.loading ? 'visible' : 'hidden' }}>
							<span style={{ animationDuration: `${planConfig.sponsorsScreen.duration}ms` }}></span>
						</div>
					</>
				)}
			</div>

			{(target.label || target.boothNo) && (
				<div className={styles.navigationTarget}>
					{target.label && <span className={styles.label}>{target.label}</span>}
					{target.boothNo && <span className={styles.boothNo}>{target.boothNo}</span>}
					{target.indicator && <span className={styles.indicator}>{target.indicator.join(', ')}</span>}
				</div>
			)}

			<header
				className={sponsorState.header && planConfig.search.allowManual ? styles.beforeSearch : styles.afterSearch}
				onKeyDown={(e) => {
					e.stopPropagation();
				}}
			>
				<div>
					{searchParams.get('noLogo') === null && state?.event && (
						<img
							className={cls(styles.logo, styles.monotoneImage)}
							src={`${process.env.RASAYESH_API_URL}/${getEventLogo(state?.event, 'fa')}`}
							alt='Event Logo'
						/>
					)}
				</div>

				{sponsorState.header && planConfig.search.allowManual && (
					<div className={styles.searchWrapper}>
						<section
							className={styles.search}
							onClick={(e) => {
								e.stopPropagation();
							}}
						>
							<input
								ref={inputSourceRef}
								type='text'
								placeholder={`غرفه مبدا را انتخاب کنید`}
								autoComplete='off'
								value={sourceSearch}
								onFocus={(e) => {
									if (e.target.value.length > 0) {
										setOpenSourceModal(true);
									}
									setOpenDestinationModal(false);
								}}
								onChange={(e) => {
									inputChangeHandler(e, 'source');
								}}
							/>

							{openSourceModal && (
								<section className={styles.companies} key={`search-${sourceSearch}`}>
									{searchSourceItems.map((item) => (
										<div
											className={styles.company}
											key={item.key}
											onClick={(e) => {
												e.preventDefault();
												handleSelect(item, 'source');
											}}
										>
											{item.label}
										</div>
									))}
								</section>
							)}
						</section>

						<section
							className={styles.search}
							onClick={(e) => {
								e.stopPropagation();
							}}
						>
							<input
								ref={inputDestinationRef}
								type='text'
								placeholder={`غرفه مقصد را انتخاب کنید`}
								autoComplete='off'
								value={destinationSearch}
								onFocus={(e) => {
									if (e.target.value.length > 0) {
										setOpenDestinationModal(true);
									}
									setOpenSourceModal(false);
								}}
								onChange={(e) => {
									inputChangeHandler(e, 'destination');
								}}
							/>

							{openDestinationModal && (
								<section className={styles.companies} key={`search-${destinationSearch}`}>
									{searchDestinationItems.map((item) => (
										<div
											className={styles.company}
											key={item.key}
											onClick={(e) => {
												e.preventDefault();
												handleSelect(item, 'destination');
											}}
										>
											{item.label}
										</div>
									))}
								</section>
							)}
						</section>

						<button onClick={navigateHandler} className={styles.searchButton}>
							<span>شروع مسیریابی</span>
							<i className='icon location-arrow-regular'></i>
						</button>
					</div>
				)}

				{target.label && (
					<div className={styles.buttonWrapper}>
						{showReplay && (
							<button onClick={navigateHandler} className={styles.searchButton}>
								<span>نمایش دوباره</span>
								<i className='icon arrow-rotate-left-regular'></i>
							</button>
						)}

						{searchParams.get('noShare') === null && (
							<button onClick={shareHandler} className={styles.searchButton}>
								<span>کپی آدرس</span>
								<i className='icon share-nodes-regular'></i>
							</button>
						)}
					</div>
				)}
			</header>

			<canvas ref={canvasRef} id='ThreeCanvas' />
		</main>
	);
}

export default PlanPage;
